import {
  DataBlockVariant,
  QueryFactory,
  useEnabledDataBlockTypes,
} from '@lmt/shared-types';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  UIConfigBlock,
  UIConfigurationType,
  UIConfigurator,
} from '@lmt/unicorn-shared/app/components/configurator/UIConfigurator';
import {
  patientInformationDataBlockTypeName,
  useDataBlockTypeTranslation,
} from '@lmt/unicorn-shared/app/components/measurements/common';
import { useVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import { UIConfigurationsActions } from '@lmt/unicorn-shared/app/features/ui-configurations/reducer';
import { getUIConfigurations } from '@lmt/unicorn-shared/app/features/ui-configurations/selector';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { localizeCaseTitle } from '@lmt/unicorn-shared/app/helpers/localization/translations';
import {
  useUniDispatch,
  useUniSelector,
} from '@lmt/unicorn-shared/app/store/types';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PatientDataBlockPanel } from './components/PatientDataBlockPanel';
import { PatientDiagnoses } from './components/PatientDiagnosesPanel';
import PatientDocumentsPanel from './components/PatientDocumentsPanel';
import { PatientInfoPanel } from './components/PatientInfo';
import { PatientVisitsPanel } from './components/PatientVisits';
import { UserCommentsPanel } from './components/UserComments';
import { defaultExcludedFileTypes } from '../cases/components/documents/common';
import { format } from 'date-fns';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTranslation } from 'react-i18next';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

interface PatientOverviewProps {
  patientId: number;
  patientOverviewIsEdit: boolean;
  setPatientOverviewEdit: (value: boolean) => void;
  editableConfigId?: number;
}

export const PatientOverview: React.FC<PatientOverviewProps> = React.memo(
  function PatientOverview(props) {
    const {
      patientId,
      patientOverviewIsEdit,
      setPatientOverviewEdit,
      editableConfigId,
    } = props;
    const i18next = useScopedTranslation('patients.overview');
    const i18nextGlobal = useTranslation();
    const dispatch = useUniDispatch();
    const modals = useModal();
    const queryClient = useQueryClient();

    const tabs = useTabs();
    const visitTypes = useVisitTypes();
    const dataBlockTypes = useEnabledDataBlockTypes();
    const translateDataBlockType = useDataBlockTypeTranslation();

    const defaultConfigId =
      useUniSelector(getUIConfigurations).patientOverviewUIConfigurationId;
    const configurationsQuery =
      QueryFactory.UIConfigurationQuery.useGetPatientOverviewConfigurationsQuery();
    useEffect(() => {
      if (configurationsQuery.data) {
        if (!defaultConfigId) {
          const defaultConfig = configurationsQuery.data.find(
            (config) => config.isDefault,
          );
          if (defaultConfig) {
            dispatch(
              UIConfigurationsActions.patientOverviewUIConfigurationIdChanged(
                defaultConfig.id,
              ),
            );
          } else {
            dispatch(
              UIConfigurationsActions.patientOverviewUIConfigurationIdChanged(
                null,
              ),
            );
          }
        }
      }
    }, [defaultConfigId, configurationsQuery.data]);

    const patientDataBlockTypes = useMemo(() => {
      return dataBlockTypes.filter(
        (dataBlockType) =>
          dataBlockType.variant === DataBlockVariant.Patient &&
          dataBlockType.value !== patientInformationDataBlockTypeName,
      );
    }, [dataBlockTypes]);

    useEffect(() => {
      const defaultConfig = configurationsQuery.data?.find(
        (config) => config.isDefault,
      );

      if (!patientOverviewIsEdit && defaultConfig) {
        invalidateQuery(defaultConfig.id).catch((e) => console.error(e));
      }

      if (!patientOverviewIsEdit && editableConfigId) {
        invalidateQuery(editableConfigId);
      }
    }, [patientOverviewIsEdit]);

    const invalidateQuery = useCallback(async (configId: number) => {
      await queryClient.invalidateQueries({
        queryKey:
          QueryFactory.UIConfigurationQuery.getConfigurationQueryKey(configId),
      });
    }, []);

    const { data: waitingList } =
      QueryFactory.AppointmentEventsQuery.useSearchVisitsQuery({
        isWaitingList: true,
      });
    const { data: calendarSettings } =
      QueryFactory.CalendarSettingsQuery.useGetQuery();

    const openCalendarTab = useCallback(
      async (date: Date) => {
        if (
          !calendarSettings ||
          !waitingList ||
          !waitingList.find((visit) => visit.start > date)
        ) {
          return;
        }
        const isConfirmed = await modals.showConfirm({
          title: i18nextGlobal.t('confirmations.waiting_list_patients.title'),
          text: i18nextGlobal.t('confirmations.waiting_list_patients.text'),
          okButtonText: i18nextGlobal.t(
            'confirmations.waiting_list_patients.ok_button',
          ),
          cancelButtonText: i18nextGlobal.t(
            'confirmations.waiting_list_patients.cancel_button',
          ),
        });
        if (isConfirmed) {
          const search = `configurationId=${
            calendarSettings.defaultCalendarView
          }&currentDay=${format(date, 'yyyy-MM-dd')}`;
          tabs.openTab(pages.calendarTab.link(), search);
        }
      },
      [waitingList, dispatch, calendarSettings?.defaultCalendarView],
    );

    const documentsQuery = QueryFactory.FileQuery.useSearchFilesQuery({
      patientId: patientId,
      excludeFileType: defaultExcludedFileTypes,
      includeTransportStatuses: true,
    });

    const visitsQuery =
      QueryFactory.AppointmentEventsQuery.useSearchVisitsQuery({
        patientId: patientId,
      });

    const dataBlocksQuery = QueryFactory.DataBlockQuery.useGetQuery(patientId);

    const caseQuery = QueryFactory.CaseQuery.useGetCasesQuery(patientId);

    const patientVisits = useMemo(() => {
      if (visitsQuery.data && caseQuery.data && dataBlocksQuery.data) {
        return visitsQuery.data
          .map((visit) => {
            const visitCase = caseQuery.data.data.find(
              (patientCase) => patientCase.id === visit.caseId,
            );
            const visitType = i18nextGlobal.t(
              (visitTypes.find((visitType) => visitType.id === visit.typeId)
                ?.value || '-') as any,
            );

            return {
              id: visit.id,
              type: visitType,
              statusId: visit.statusId,
              caseId: visitCase?.id ?? NaN,
              caseTitle: visitCase ? localizeCaseTitle(visitCase) : '-',
              startDate: visit.start,
              endDate: visit.end,
              dataBlocks: dataBlocksQuery.data.data
                .filter(
                  (dataBlock) =>
                    dataBlock.visitId === visit.id &&
                    dataBlockTypes.some((type) => type.id === dataBlock.typeId),
                )
                .map((dataBlock) =>
                  translateDataBlockType(
                    dataBlockTypes.find((x) => x.id === dataBlock.typeId)!,
                  ),
                ),
            };
          })
          .sort((a, b) => b.startDate!.getTime() - a.startDate!.getTime());
      } else return [];
    }, [visitsQuery.data, caseQuery.data, dataBlocksQuery.data]);

    const infoBlock = <PatientInfoPanel patientId={patientId} />;

    const documentsBlock = documentsQuery.data && (
      <PatientDocumentsPanel documents={documentsQuery.data} />
    );

    const patientDataBlocks = useMemo(() => {
      const pDataBlocks: { [key: string]: UIConfigBlock } = {};
      for (const dataBlockType of patientDataBlockTypes) {
        const id = dataBlockType.id.toString();
        const patientDataBlockComponent = (
          <PatientDataBlockPanel
            patientId={patientId}
            dataBlockType={dataBlockType}
          />
        );
        pDataBlocks[id] = {
          id: id,
          component: patientDataBlockComponent,
          minW: 3,
          minH: 3,
          w: 4,
          h: 3,
          isHidden: dataBlockType.isDeactivated,
        };
      }
      return pDataBlocks;
    }, [patientDataBlockTypes]);

    const defaultBlocks: { [key: string]: UIConfigBlock } = useMemo(() => {
      return {
        patientInfo: {
          id: 'patientInfo',
          component: infoBlock,
          static: true,
          w: 5,
          h: 3,
        },
        diagnoses: {
          id: 'diagnoses',
          component: <PatientDiagnoses patientDiagnoses={[]} />,
          minW: 2,
          maxW: 6,
          minH: 3,
          w: 2,
          h: 3,
        },
        comments: {
          id: 'comments',
          component: <UserCommentsPanel patientId={patientId} />,
          minW: 3,
          maxW: 6,
          minH: 3,
          w: 5,
          h: 3,
        },
        visits: {
          id: 'visits',
          component: (
            <PatientVisitsPanel
              openCalendarTab={openCalendarTab}
              patientId={patientId}
              patientVisits={patientVisits}
            />
          ),
          minW: 6,
          maxW: 12,
          minH: 3,
          w: 9,
          h: 6,
        },
        documents: {
          id: 'documents',
          component: documentsBlock,
          minW: 3,
          maxW: 6,
          minH: 3,
          w: 3,
          h: 6,
        },
      };
    }, [infoBlock, documentsBlock, patientVisits]);

    const blocks: { [key: string]: UIConfigBlock } = useMemo(() => {
      return Object.assign({}, defaultBlocks, patientDataBlocks);
    }, [defaultBlocks, patientDataBlocks]);

    return (
      <Loading
        loading={
          documentsQuery.isLoading ||
          visitsQuery.isLoading ||
          dataBlocksQuery.isLoading ||
          caseQuery.isLoading
        }
        title={i18next.t('loading')}
      >
        <UIConfigurator
          title={i18next.t('editor_title')}
          blocks={blocks}
          isEditMode={patientOverviewIsEdit}
          configType={UIConfigurationType.PatientOverview}
          closeEditMode={setPatientOverviewEdit}
          configId={
            patientOverviewIsEdit
              ? editableConfigId
              : (defaultConfigId ?? undefined)
          }
        />
      </Loading>
    );
  },
);
